/**
 * WaitlistPage (`/waitlist`) — Bayenah closed-beta access request.
 *
 * Flow:
 *   1. Form (full_name / work_email / company / role / team_size /
 *      monthly_volume / challenge) — corporate email enforced.
 *   2. POST /api/waitlist/request → confirmation screen with team-invite
 *      input (up to 3 colleagues sharing the same domain).
 *   3. POST /api/waitlist/invite → green check + final thank-you copy.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, Loader2, Plus, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const BACKEND = getBackendBaseUrl();

const PERSONAL = new Set([
  'gmail.com', 'googlemail.com',
  'hotmail.com', 'live.com', 'outlook.com', 'msn.com',
  'yahoo.com', 'yahoo.co.uk', 'ymail.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'protonmail.com', 'proton.me',
  'mail.com', 'gmx.com', 'zoho.com',
  'qq.com', '163.com', '126.com',
]);

// Display labels for each enum value. The *values* are owned by the
// backend and fetched at request time from GET /api/waitlist/options —
// the SPA renders whatever the backend publishes so silent 422 drift
// on `role` / `team_size` / `monthly_volume` cannot occur. Labels
// stay client-side (UX copy); a missing key humanizes gracefully.
const ROLE_LABELS = {
  general_counsel:    'General Counsel / Head of Legal',
  legal_counsel:      'Legal Counsel / Lawyer',
  legal_operations:   'Legal Operations',
  compliance_officer: 'Compliance Officer',
  contract_manager:   'Contract Manager',
  law_firm:           'Law Firm Partner / Associate',
  other:              'Other',
};
const TEAM_SIZE_LABELS = {
  'solo': 'Just me',
  '2-5':  '2–5 lawyers',
  '6-15': '6–15 lawyers',
  '15+':  '15+ lawyers',
};
const VOLUME_LABELS = {
  '<10':    'Under 10 contracts/month',
  '10-50':  '10–50 contracts/month',
  '50-200': '50–200 contracts/month',
  '200+':   '200+ contracts/month',
};

// Fallback ordered vocabularies — used only if the /options fetch
// fails so the form is never blocked. Kept in lock-step with
// backend/routes/waitlist.py (ROLE_OPTIONS / TEAM_SIZE_OPTIONS /
// VOLUME_OPTIONS) as a belt-and-braces safety net.
const FALLBACK_OPTIONS = {
  role:            ['general_counsel', 'legal_counsel', 'legal_operations', 'compliance_officer', 'contract_manager', 'law_firm', 'other'],
  team_size:       ['solo', '2-5', '6-15', '15+'],
  monthly_volume:  ['<10', '10-50', '50-200', '200+'],
};

const humanize = (v) => v.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const toOptions = (values, labels) => values.map((v) => ({ v, en: labels[v] || humanize(v) }));

const domainOf = (em) => (em.split('@')[1] || '').trim().toLowerCase();
const isCorporate = (em) => {
  const d = domainOf(em);
  return d && !PERSONAL.has(d);
};

export default function WaitlistPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [step, setStep] = useState('form');                // 'form' | 'confirm'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);        // server response
  // Vocabularies fetched from backend at mount — kills silent 422 drift.
  const [vocab, setVocab] = useState(FALLBACK_OPTIONS);
  const [form, setForm] = useState({
    full_name: '',
    work_email: '',
    company_name: '',
    role: '',
    team_size: '',
    monthly_volume: '',
    challenge: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/waitlist/options`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (Array.isArray(data?.role) && Array.isArray(data?.team_size) && Array.isArray(data?.monthly_volume)) {
          setVocab({
            role: data.role,
            team_size: data.team_size,
            monthly_volume: data.monthly_volume,
          });
        }
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const ROLES       = useMemo(() => toOptions(vocab.role,           ROLE_LABELS),      [vocab.role]);
  const TEAM_SIZES  = useMemo(() => toOptions(vocab.team_size,      TEAM_SIZE_LABELS), [vocab.team_size]);
  const VOLUMES     = useMemo(() => toOptions(vocab.monthly_volume, VOLUME_LABELS),    [vocab.monthly_volume]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = useMemo(() => (
    form.full_name.trim().length >= 2 &&
    form.work_email.includes('@') &&
    form.company_name.trim().length >= 2 &&
    form.role && form.team_size && form.monthly_volume
  ), [form]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isCorporate(form.work_email)) {
      setError('Please use your corporate email address.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/waitlist/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Request failed');
      setSubmitted({ ...data, ...form });
      setStep('confirm');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.message || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="waitlist-page"
      dir="ltr"
      className="min-h-screen w-full bg-[#F5F3EF] text-[#0A0A0A]"
      style={{ fontFamily: 'Inter, ui-sans-serif, system-ui' }}
    >
      <header className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <a href="/" className="text-[15px] tracking-tight font-semibold">Bayenah</a>
        <a
          href="/"
          className="text-[12.5px] text-[#666] hover:text-[#0A0A0A] transition"
          data-testid="waitlist-back-home"
        >
          ← Back to home
        </a>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-4 pb-20">
        {step === 'form' && (
          <FormView
            form={form} set={set} submit={submit}
            valid={valid} submitting={submitting} error={error}
            isAr={isAr}
            roles={ROLES} teamSizes={TEAM_SIZES} volumes={VOLUMES}
          />
        )}
        {step === 'confirm' && submitted && (
          <ConfirmView data={submitted} />
        )}
      </section>
    </main>
  );
}

function FormView({ form, set, submit, valid, submitting, error, isAr, roles, teamSizes, volumes }) {
  const emailError = form.work_email && !isCorporate(form.work_email)
    ? 'Personal email addresses are not accepted. Please use your corporate email.'
    : '';

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="space-y-7">
      <header className="space-y-3" dir={isAr ? 'rtl' : 'ltr'}>
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-[#666]" data-testid="waitlist-eyebrow">
          {isAr ? 'الوصول المغلق' : 'Closed Beta'}
        </p>
        <h1
          className="text-3xl sm:text-4xl font-serif font-medium leading-[1.1]"
          data-testid="waitlist-headline"
          style={isAr ? { fontFamily: 'var(--font-serif-ar), serif' } : undefined}
        >
          {isAr ? 'اطلب الوصول المبكر.' : 'Request early access.'}
        </h1>
        <p
          className="text-[14px] text-[#555] leading-relaxed max-w-xl"
          data-testid="waitlist-sub"
        >
          {isAr
            ? 'بينة في مرحلة النشر الفعلي مع مجموعة مختارة من الفرق القانونية في المؤسسات السعودية. أخبرنا عن فريقك وسنتواصل معك.'
            : 'Bayenah is in active deployment with a select group of Saudi enterprise legal teams. Tell us about your team and we will be in touch.'}
        </p>
      </header>

      <Field label="Full name" required>
        <input
          value={form.full_name} onChange={set('full_name')}
          autoComplete="name"
          className="w-full bg-white border border-[#E5E1D8] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A]"
          data-testid="waitlist-full-name"
          required
        />
      </Field>

      <Field
        label="Work email" required
        hint="Personal email addresses are not accepted. We use your company domain to verify your organization."
        error={emailError}
      >
        <input
          type="email" value={form.work_email} onChange={set('work_email')}
          autoComplete="email" placeholder="you@company.com"
          className={cn(
            'w-full bg-white border rounded-md px-3 py-2.5 text-[14px] focus:outline-none',
            emailError ? 'border-[#B91C1C] focus:border-[#B91C1C]' : 'border-[#E5E1D8] focus:border-[#0A0A0A]',
          )}
          data-testid="waitlist-work-email"
          required
        />
      </Field>

      <Field label="Company name" required>
        <input
          value={form.company_name} onChange={set('company_name')}
          className="w-full bg-white border border-[#E5E1D8] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A]"
          data-testid="waitlist-company"
          required
        />
      </Field>

      <Field label="Your role" required>
        <Select value={form.role} onChange={set('role')} options={roles} testId="waitlist-role" />
      </Field>

      <Field label="Team size" required>
        <Select value={form.team_size} onChange={set('team_size')} options={teamSizes} testId="waitlist-team-size" />
      </Field>

      <Field label="Monthly contract volume (approximate)" required>
        <Select value={form.monthly_volume} onChange={set('monthly_volume')} options={volumes} testId="waitlist-volume" />
      </Field>

      <Field label="What is your biggest legal workflow challenge?" optional>
        <textarea
          rows={3} value={form.challenge} onChange={set('challenge')}
          className="w-full bg-white border border-[#E5E1D8] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A] resize-none"
          data-testid="waitlist-challenge"
        />
      </Field>

      {error && (
        <div className="text-[13px] text-[#B91C1C]" data-testid="waitlist-error">{error}</div>
      )}

      <button
        type="submit"
        disabled={!valid || submitting}
        className={cn(
          'w-full inline-flex items-center justify-center gap-2 rounded-full text-[14px] py-3 transition',
          (valid && !submitting)
            ? 'bg-[#0A0A0A] text-white hover:bg-black/85'
            : 'bg-[#0A0A0A]/50 text-white cursor-not-allowed',
        )}
        data-testid="waitlist-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {submitting
          ? (isAr ? 'جارٍ الإرسال…' : 'Submitting…')
          : (isAr ? 'طلب الوصول' : 'Request Access')}
      </button>

      <p className="text-[11px] text-[#888] leading-relaxed">
        By requesting access, you agree to our <a href="/terms" className="underline">Terms of Service</a>{' '}
        and <a href="/privacy" className="underline">Privacy Policy</a>. We will contact you within 2 business days.
      </p>
    </form>
  );
}

function ConfirmView({ data }) {
  const domain = data.company_domain || domainOf(data.work_email);
  const [colleagues, setColleagues] = useState(['', '', '']);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null);

  const setColleague = (i) => (e) => {
    const v = e.target.value;
    setColleagues((cur) => cur.map((x, idx) => idx === i ? v : x));
  };

  const validRows = colleagues
    .map((em) => em.trim().toLowerCase())
    .filter((em) => em && em.includes('@'));

  const issueFor = (em) => {
    const lower = em.trim().toLowerCase();
    if (!lower) return '';
    const d = domainOf(lower);
    if (PERSONAL.has(d)) return 'Use your corporate email.';
    if (d && d !== domain) return `Please use a @${domain} email address.`;
    if (lower === data.work_email.toLowerCase()) return "You're already on the list.";
    return '';
  };

  const sendInvites = async () => {
    const cleaned = validRows.filter((em) => !issueFor(em));
    if (cleaned.length === 0) return;
    setAdding(true);
    try {
      const r = await fetch(`${BACKEND}/api/waitlist/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ work_email: data.work_email, colleagues: cleaned }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json?.detail || 'Invite failed');
      setResult(json);
    } catch (e) {
      setResult({ error: e.message || 'Could not invite colleagues.' });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="waitlist-confirm" className="space-y-10">
      <header className="space-y-3">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#E5E1D8] bg-white px-3 py-1 text-[11.5px] uppercase tracking-[0.16em] text-[#0A0A0A]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A0A]" /> On the list
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif font-medium leading-[1.1]">
          You're on the list.
        </h1>
        <p className="text-[14px] text-[#555] leading-relaxed max-w-xl">
          We've received your request and will review it shortly. Bayenah is being rolled out to legal
          teams in stages — we'll contact you at <strong>{data.work_email}</strong> when your access is ready.
        </p>
      </header>

      <section className="border-t border-[#E5E1D8] pt-8 space-y-4" data-testid="waitlist-team-invite">
        <h2 className="text-[20px] font-serif font-medium">Bring your team.</h2>
        <p className="text-[13.5px] text-[#555] leading-relaxed max-w-xl">
          Bayenah works best when your whole legal team has access at once. Add up to 3 colleagues
          from <strong>@{domain}</strong> and we'll activate your team together.
        </p>
        <div className="space-y-2">
          {colleagues.map((em, i) => {
            const issue = issueFor(em);
            return (
              <div key={i} className="flex items-start gap-2">
                <div className="flex-1">
                  <input
                    type="email" value={em} onChange={setColleague(i)}
                    placeholder={`colleague@${domain}`}
                    className={cn(
                      'w-full bg-white border rounded-md px-3 py-2 text-[13.5px] focus:outline-none',
                      issue ? 'border-[#B91C1C] focus:border-[#B91C1C]' : 'border-[#E5E1D8] focus:border-[#0A0A0A]',
                    )}
                    data-testid={`waitlist-colleague-${i}`}
                  />
                  {issue && <p className="text-[11px] text-[#B91C1C] mt-1">{issue}</p>}
                </div>
              </div>
            );
          })}
        </div>

        {result?.error && (
          <p className="text-[12.5px] text-[#B91C1C]" data-testid="waitlist-invite-error">{result.error}</p>
        )}
        {result?.accepted && (
          <p className="text-[12.5px] text-[#0A0A0A] inline-flex items-center gap-1.5" data-testid="waitlist-invite-success">
            <Check className="w-3.5 h-3.5" />
            {result.accepted.length} colleague{result.accepted.length === 1 ? '' : 's'} added.
            Your team will receive access together.
          </p>
        )}
        {result?.rejected?.length > 0 && (
          <p className="text-[12px] text-[#B45309]" data-testid="waitlist-invite-rejected">
            {result.rejected.length} address{result.rejected.length === 1 ? ' was' : 'es were'} skipped (wrong domain or personal email).
          </p>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={sendInvites}
            disabled={adding || validRows.filter((em) => !issueFor(em)).length === 0}
            className={cn(
              'inline-flex items-center justify-center gap-2 rounded-full text-[13px] px-5 py-2 transition',
              'bg-[#0A0A0A] text-white hover:bg-black/85 disabled:bg-[#0A0A0A]/40 disabled:cursor-not-allowed',
            )}
            data-testid="waitlist-invite-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {adding ? 'Sending…' : 'Add colleagues'}
          </button>
          <a href="/" className="text-[12.5px] text-[#666] hover:text-[#0A0A0A]">
            Skip for now — I'll add teammates later
          </a>
        </div>
      </section>

      <p className="text-[12px] text-[#888]">
        We'll be in touch at <strong>{data.work_email}</strong> within 2 business days.
      </p>
    </div>
  );
}

function Field({ label, required, optional, hint, error, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[12.5px] text-[#0A0A0A]">
        {label}
        {required && <span className="text-[#B91C1C]"> *</span>}
        {optional && <span className="text-[#888]"> (optional)</span>}
      </span>
      {children}
      {hint && !error && (<p className="text-[11px] text-[#888] leading-relaxed">{hint}</p>)}
      {error && (<p className="text-[11px] text-[#B91C1C] leading-relaxed">{error}</p>)}
    </label>
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select
      value={value} onChange={onChange}
      className="w-full bg-white border border-[#E5E1D8] rounded-md px-3 py-2.5 text-[14px] focus:outline-none focus:border-[#0A0A0A]"
      data-testid={testId}
      required
    >
      <option value="" disabled>Select…</option>
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.en}</option>
      ))}
    </select>
  );
}
